import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Package, Compass, DownloadSimple, Plus, Trash, ArrowRight, ShieldStar, CalendarBlank, Link as LinkIcon, CursorClick
} from "@phosphor-icons/react";
import { toast } from "sonner";
import BankingPlaceholder from "@/components/BankingPlaceholder";
import usePageMeta from "@/hooks/usePageMeta";

export default function Dashboard() {
  usePageMeta(
    "Commander Deck",
    "Your EIGOO Commander Deck — download vault, active Sovereign Blueprints, and mission progress tracking."
  );
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [blueprints, setBlueprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [affiliateStats, setAffiliateStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/vault/purchases").then((r) => setPurchases(r.data.purchases)),
      api.get("/blueprint/list").then((r) => setBlueprints(r.data.blueprints)),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api
      .get("/affiliates/stats")
      .then((r) => setAffiliateStats(r.data))
      .catch(() => setAffiliateStats({ links: [], total_links: 0, total_clicks: 0 }));
  }, [user]);

  const deleteBlueprint = async (id) => {
    if (!window.confirm("Decommission this blueprint?")) return;
    try {
      await api.delete(`/blueprint/${id}`);
      setBlueprints(blueprints.filter((b) => b.id !== id));
      toast.success("Blueprint decommissioned.");
    } catch (e) { toast.error("Failed to decommission"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12 pb-8 border-b border-white/5">
        <div>
          <span className="overline">// Commander Deck // Bridge {user?.id?.slice(0, 8)}</span>
          <h1 className="heading-display text-4xl md:text-5xl mt-3" data-testid="dashboard-welcome">
            Welcome, <span className="text-[#FFD700]">{user?.name}</span>
          </h1>
          <p className="text-sm text-white/50 font-light mt-2 font-mono uppercase tracking-widest">
            Sovereign Status: Operational
          </p>
        </div>
        <Link to="/blueprint" data-testid="new-blueprint-btn" className="btn-gold px-6 py-3 text-xs inline-flex items-center gap-3">
          <Plus size={16} weight="bold" />
          New Blueprint
        </Link>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
        <StatCard label="Provisions Owned" value={purchases.length} icon={Package} />
        <StatCard label="Active Blueprints" value={blueprints.length} icon={Compass} />
        <StatCard label="Rank" value="Commander" icon={ShieldStar} mono />
      </div>

      {/* DOWNLOAD VAULT */}
      <section className="mb-16" data-testid="vault-section">
        <div className="flex items-center justify-between mb-6">
          <h2 className="heading-display text-2xl">Download Vault</h2>
          <div className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/40">
            {purchases.length} provisions secured
          </div>
        </div>

        {loading ? (
          <div className="surface-card p-8 text-center text-white/40 overline">Decrypting vault...</div>
        ) : purchases.length === 0 ? (
          <div className="surface-card p-12 text-center">
            <Package size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/50 mb-6">No provisions in your vault yet.</p>
            <Link to="/catalog" className="btn-ghost px-6 py-3 text-xs inline-flex items-center gap-2">
              Browse Arsenal <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {purchases.map((p) => (
              <div key={p.id} data-testid={`vault-item-${p.product_id}`} className="surface-card p-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-14 h-14 border border-[#FFD700]/30 flex items-center justify-center flex-shrink-0">
                    <DownloadSimple size={22} weight="duotone" className="text-[#FFD700]" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-white truncate">{p.product?.title || p.product_id}</div>
                    <div className="font-mono text-[0.65rem] uppercase tracking-widest text-white/40 mt-1">
                      {new Date(p.purchased_at).toLocaleDateString()} // ${p.amount}
                    </div>
                  </div>
                </div>
                <button
                  className="btn-ghost px-4 py-2 text-[0.65rem] flex-shrink-0"
                  onClick={() => toast.info("Demo: Download link would generate here.")}
                  data-testid={`download-btn-${p.product_id}`}
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* BLUEPRINTS */}
      <section data-testid="blueprints-section">
        <div className="flex items-center justify-between mb-6">
          <h2 className="heading-display text-2xl">Sovereign Blueprints</h2>
          <div className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/40">
            {blueprints.length} active
          </div>
        </div>

        {blueprints.length === 0 ? (
          <div className="surface-card p-12 text-center">
            <Compass size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/50 mb-2">No blueprints commissioned yet.</p>
            <p className="text-xs text-white/40 mb-6">Generate one tailored to any business sector.</p>
            <Link to="/blueprint" className="btn-gold px-6 py-3 text-xs inline-flex items-center gap-2">
              <Plus size={14} /> Generate First Blueprint
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {blueprints.map((b) => {
              const progress = b.progress || {};
              const totalMilestones = (b.blueprint?.stages || []).reduce((sum, s) => sum + s.milestones.length, 0);
              const completedCount = Object.values(progress).reduce((sum, stage) => sum + Object.values(stage).filter(Boolean).length, 0);
              const pct = totalMilestones ? Math.round((completedCount / totalMilestones) * 100) : 0;
              return (
                <div key={b.id} data-testid={`blueprint-card-${b.id}`} className="surface-card p-5 flex flex-col">
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="overline text-[0.6rem]">// {b.business_type}</div>
                    <button onClick={() => deleteBlueprint(b.id)} className="text-white/30 hover:text-red-400" data-testid={`delete-blueprint-${b.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                  <h3 className="font-bold text-white mb-2 line-clamp-2">{b.blueprint?.summary || b.business_type}</h3>
                  <div className="flex items-center gap-2 text-[0.65rem] font-mono uppercase tracking-widest text-white/40 mb-4">
                    <CalendarBlank size={12} />
                    {new Date(b.created_at).toLocaleDateString()}
                  </div>
                  <div className="mt-auto">
                    <div className="flex justify-between text-[0.6rem] font-mono uppercase tracking-widest text-white/50 mb-1">
                      <span>Progress</span><span className="text-[#FFD700]">{pct}%</span>
                    </div>
                    <div className="h-1 bg-white/10 mb-4">
                      <div className="h-full bg-[#FFD700] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <Link to={`/blueprint/${b.id}`} className="block w-full text-center btn-ghost py-2.5 text-[0.7rem]">
                      Open Blueprint
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
      {/* AFFILIATE STATS — Admin only */}
      {user?.role === "admin" && affiliateStats && (
        <section className="mt-16" data-testid="affiliate-stats-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="heading-display text-2xl">Affiliate Click Log</h2>
            <div className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/40">
              {affiliateStats.total_links} links // {affiliateStats.total_clicks} clicks
            </div>
          </div>

          {affiliateStats.links.length === 0 ? (
            <div className="surface-card p-10 text-center">
              <LinkIcon size={40} weight="duotone" className="text-white/20 mx-auto mb-3" />
              <p className="text-white/50 text-sm">No tracked vendor clicks yet.</p>
              <p className="text-xs text-white/40 mt-1">Niche Arsenal items auto-register on first view.</p>
            </div>
          ) : (
            <div className="surface-card overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/5 overline text-[0.55rem]">
                <div className="col-span-6">// Vendor</div>
                <div className="col-span-3">// Source</div>
                <div className="col-span-3 text-right">// Clicks</div>
              </div>
              {affiliateStats.links.slice(0, 50).map((l) => (
                <div
                  key={l.code}
                  data-testid={`affiliate-row-${l.code}`}
                  className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  <div className="col-span-6 min-w-0">
                    <div className="font-semibold text-sm text-white truncate">{l.label || l.vendor_url}</div>
                    <a
                      href={l.vendor_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[0.6rem] uppercase tracking-widest text-white/40 truncate block hover:text-[#FFD700]/80"
                    >
                      {l.vendor_url}
                    </a>
                  </div>
                  <div className="col-span-3 font-mono text-[0.65rem] uppercase tracking-widest text-white/50 self-center">
                    {l.source || "—"}
                  </div>
                  <div className="col-span-3 text-right self-center">
                    <span className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-[#FFD700]">
                      <CursorClick size={13} weight="duotone" /> {l.clicks || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, mono }) => (
  <div className="surface-card p-6 flex items-center gap-5">
    <div className="w-12 h-12 border border-[#FFD700]/30 flex items-center justify-center">
      <Icon size={22} weight="duotone" className="text-[#FFD700]" />
    </div>
    <div>
      <div className="overline text-[0.6rem] mb-1">{label}</div>
      <div className={`${mono ? "font-mono text-lg uppercase tracking-widest" : "text-3xl font-bold"} text-white`}>{value}</div>
    </div>
  </div>
);
